import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { userMixin } from '@app/users/mixins/user-mixin';
import { AddUsersDialogComponent, AddUsersDialogData } from '@components/add-users-dialog/1.0';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { forkJoin, mergeMap, of, Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { CustomerUser, Segment, SegmentUsers, User } from '@generated/tcea/models';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { SegmentsService } from '@services/segments/1.0/segments.service';
import { AuthService } from '@auth0/auth0-angular';
import { MatDialog } from '@angular/material/dialog';
import { GetService } from '@services/utilities/1.0/get.service';
import { NgIf } from '@angular/common';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MessageComponent } from '../message/1.0/message.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { ButtonComponent } from '../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-edit-segment',
  templateUrl: './edit-segment.component.html',
  styleUrls: ['./edit-segment.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    MessageComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ButtonComponent,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
  ],
})
export class EditSegmentComponent extends userMixin(FormCanDeactivate) implements OnInit {
  @ViewChild('idInput', { read: ElementRef }) idInput: ElementRef;

  form: UntypedFormGroup;
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  messageType = MessageType; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  displayColumns = ['segmentName', 'email', 'id', 'action'];
  dataSource: MatTableDataSource<CustomerUser> = new MatTableDataSource<CustomerUser>();
  lastSavedSegment: Segment;
  isLoading = false;
  usersLoading = false;
  showLoadError = false;
  showSaveError = false;
  showSaveUsersError = false;
  sort = { direction: '', field: '' };
  isSaving = false;
  segmentsReadOnly = true;
  nameChanges: Subscription;
  newSegment = true;
  createSegmentSuccess = false;
  formSubmitted = false;
  brandId: string;
  user: User;
  initialUserList: CustomerUser[] = [];
  newUserList: CustomerUser[] = [];

  subscription: Subscription;

  private unsubscribe = new Subject<void>();

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private permissionsService: PermissionsService,
    private segmentsService: SegmentsService,
  ) {
    super();
    this.segmentsReadOnly =
      this.permissionsService.getPermission('coupon-management:read') &&
      !this.permissionsService.getPermission('coupon-management:write');
    this.form = this.formBuilder.group({
      segmentName: new UntypedFormControl('', Validators.required),
      id: new UntypedFormControl('', Validators.required),
    });

    this.route?.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.newSegment = true;

      this.segmentsReadOnly ? this.form.disable() : this.form.enable();

      if (params?.id) {
        this.newSegment = false;
        const state = this.router.getCurrentNavigation()?.extras?.state;
        if (state?.data) {
          this.lastSavedSegment = Object.assign({}, state.data);
          this.form.patchValue({
            ...this.lastSavedSegment,
          });
          this.loadUsersForSegment();
        } else {
          this.isLoading = true;
          this.loadSegment(params?.id);
        }

        this.form.get('id').disable();
      }
    });
  }

  ngOnInit(): void {
    this.authService.user$.subscribe((user) => {
      this.user = user;
    });

    if (this.newSegment) {
      this.nameChanges = this.form.get('segmentName').valueChanges.subscribe((segmentName) => {
        const newId = segmentName
          // trimming edges
          .trim()
          // replacing duplicated spaces
          .replace(/ +/g, ' ')
          // replacing spaces with dashes
          .replace(/ /g, '-')
          // keeping only aplhanumerical and dashes characters
          .replace(/[^a-zA-Z0-9-]/g, '')
          // replacing duplicated dashes
          .replace(/-+/g, '-');

        this.form.get('id').setValue(newId);
      });

      this.form.get('id').valueChanges.subscribe((segmentId) => this.handleIdInput(segmentId));
    }
  }

  onSortChange(sort): void {
    const data: Array<CustomerUser> = this.dataSource.data.slice();

    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc: boolean = sort.direction === 'asc';
      const valSortProperty = sort.active;
      const aValue =
        valSortProperty === 'segmentName'
          ? `${a?.firstName} ${a.lastName}`
          : GetService.get(a, valSortProperty);
      const bValue =
        valSortProperty === 'segmentName'
          ? `${b?.firstName} ${b.lastName}`
          : GetService.get(b, valSortProperty);
      return aValue && bValue ? this.compare(aValue, bValue, isAsc) : -1;
    });

    this.dataSource = new MatTableDataSource<CustomerUser>(this.dataSource.data);
  }

  compare(a: any, b: any, isAsc: boolean): number {
    if (isAsc) {
      return a > b ? 1 : b > a ? -1 : 0;
    } else {
      return a < b ? 1 : b < a ? -1 : 0;
    }
  }

  /**
   * Loads segment for the id provided
   * @param id
   */
  loadSegment(id: string) {
    const brandId = window.sessionStorage.getItem('brand_id');

    this.segmentsService.getSegmentById(id, brandId).subscribe({
      next: (segment: Segment) => {
        this.lastSavedSegment = Object.assign({}, segment);
        this.form.patchValue({
          ...this.lastSavedSegment,
        });
        this.isLoading = false;
        this.loadUsersForSegment();
      },
      error: () => {
        this.showLoadError = true;
        this.isLoading = false;
      },
    });
  }

  loadUsersForSegment() {
    const brandId = window.sessionStorage.getItem('brand_id');

    this.usersLoading = true;
    this.segmentsService
      .getUsersForSegment(brandId, this.lastSavedSegment.id)
      .pipe(
        mergeMap((segmentUsers: SegmentUsers) => {
          const params = (segmentUsers.userIds || []).reduce((params, userId) => {
            params.append('userId', userId);
            return params;
          }, new URLSearchParams());

          if (!params.toString()) {
            return of([]);
          }

          return this.segmentsService.getCustomerUsers(brandId, `?${params.toString()}`);
        }),
      )
      .subscribe({
        next: (customerUsers: CustomerUser[]) => {
          this.initialUserList = customerUsers;
          this.newUserList = customerUsers;
          this.dataSource.data = this.initialUserList;
          this.isLoading = false;
          this.usersLoading = false;
        },
        error: () => {
          this.showLoadError = true;
          this.isLoading = false;
          this.usersLoading = false;
        },
        complete: () => {
          this.isLoading = false;
          this.usersLoading = false;
        },
      });
  }

  handleIdInput(value) {
    const input = this.idInput.nativeElement;

    input.value = value
      // trimming edges
      .trim()
      // replacing duplicated spaces
      .replace(/ +/g, ' ')
      // replacing spaces with dashes
      .replace(/ /g, '-')
      // keeping only aplhanumerical and dashes characters
      .replace(/[^a-zA-Z0-9-]/g, '')
      // replacing duplicated dashes
      .replace(/-+/g, '-');
  }

  onCancel() {
    this.form.reset(this.lastSavedSegment);
    this.dataSource.data = this.initialUserList;
    this.newUserList = this.initialUserList.slice();
  }

  onSave() {
    const brandId = window.sessionStorage.getItem('brand_id');
    this.showSaveError = false;
    this.formSubmitted = true;
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.isSaving = true;
      const saveData = {
        createdBy: this.user.email,
        tenant: brandId,
        userCount: this.dataSource.data.length,
        // needed because if field is disabled, form.value won't return that
        id: this.form.get('id').value,
        ...this.form.value,
      };

      const obs = this.newSegment ? this.create(brandId, saveData) : this.update(brandId, saveData);

      obs.subscribe({
        next: (segmentToHandle) => {
          const addUsersObs = this.newUserList.reduce((acc, user) => {
            if (!this.initialUserList.includes(user)) {
              acc.push(
                this.segmentsService.addSegmentUser(brandId, segmentToHandle.id, user.userId),
              );
            }

            return acc;
          }, []);
          const removeUsersObs = this.initialUserList.reduce((acc, user) => {
            if (!this.newUserList.includes(user)) {
              acc.push(
                this.segmentsService.removeSegmentUser(brandId, segmentToHandle.id, user.userId),
              );
            }

            return acc;
          }, []);

          const usersObs = forkJoin([...addUsersObs, ...removeUsersObs]);

          usersObs.subscribe({
            next: () => {
              this.form.markAsPristine();
              this.isSaving = false;
            },
            error: () => {
              this.isSaving = false;
              this.showSaveUsersError = true;
            },
            complete: () => {
              this.form.markAsPristine();
              this.isSaving = false;
              this.onBack();
            },
          });
        },
        error: this.handleSaveError.bind(this),
      });
    }
  }

  create(brandId, saveData) {
    return this.segmentsService.create(brandId, saveData).pipe(first());
  }

  update(brandId, saveData) {
    return this.segmentsService.update(brandId, saveData.id, saveData).pipe(first());
  }

  addUsers() {
    const brandId = window.sessionStorage.getItem('brand_id');
    const dialogData: AddUsersDialogData = {
      brandId: brandId,
      selectedList: this.newUserList,
    };

    const dialogRef = this.dialog.open(AddUsersDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((newUserList: CustomerUser[]) => {
      // newUserList undefined means it was cancelled
      if (newUserList) {
        this.newUserList = newUserList.slice();
        this.dataSource.data = this.newUserList;
        this.form.markAsDirty();
      }
    });
  }

  deleteUser(user) {
    this.newUserList = this.newUserList.filter((selectedUser) => selectedUser !== user);
    this.dataSource.data = this.newUserList;
    this.form.markAsDirty();
  }

  onBack() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  handleSaveError() {
    this.showSaveError = true;
    this.isSaving = false;
  }
}
